/**
 * User Service
 *
 * Thin wrapper around the user-management endpoints used by the quick
 * "Add New User" flow (the global header button + AddUserModal). Managers,
 * admins, and super admins use this to onboard a tech or individual who can
 * then be assigned to work orders and inspections.
 *
 * Backend endpoints:
 *	 GET	/api/v1/users/available-roles  — roles the current user may assign
 *	 GET	/api/v1/companies			   — companies for the primary/other pickers
 *	 POST	/api/v1/users				   — create a user (+ welcome email)
 *
 * The shared `api` client snakifies outgoing bodies, so this service speaks
 * camelCase (e.g. `firstName` → `first_name`, `companyId` → `company_id`).
 * Responses are NOT auto-camelized by the client, so we normalize them here.
 */

import { api, camelizeKeys } from './api';

/** Payload for creating a user. camelCase keys are converted to snake_case by the api client. */
export interface CreateUserPayload {
	firstName: string;
	lastName: string;
	/** A subcontractor's own business name ("Cyclone Window Cleaning"). */
	subcontractorCompany?: string | null;
	/** Login identifier. Null for the CSR / JSR / Other roles, which can be
	 *  created from a name alone — those people simply can't sign in. */
	email?: string | null;
	phone?: string;
	/** Denormalized UI role label (the users.role column). */
	role: string;
	/** Authoritative role slugs assigned via the user_roles junction table. */
	roles: string[];
	/** Temporary password to set. We send the one shown in the modal so the email
	 *  matches. Omitted for users created without an email — there'd be nothing
	 *  to sign in with and nowhere to send it. */
	password?: string;
	/** Primary business (company) the user belongs to. */
	companyId?: number | null;
	/** Additional businesses the user can also access. */
	additionalCompanyIds?: number[];
}

/** Minimal shape of a created user returned to the UI. */
export interface CreatedUser {
	id: number;
	firstName: string;
	lastName: string;
	/** Null when the user was created without one (CSR / JSR / Other). */
	email: string | null;
	phone: string | null;
	role: string;
}

/** Result of a create call: the user plus the temp password to surface to the creator. */
export interface CreateUserResult {
	user: CreatedUser;
	/** The temporary password to display/share. */
	tempPassword: string | null;
}

/** A company option for the primary/other-business pickers. */
export interface CompanyOption {
	id: number;
	name: string;
}

export const userService = {
	/**
	 * GET /users/available-roles — the set of role slugs the current user is
	 * allowed to assign (server-enforced by their own role).
	 */
	async getAssignableRoles(): Promise<string[]> {
		const res = await api.get<{ data: string[] }>('/users/available-roles');
		return res.data?.data ?? [];
	},

	/**
	 * GET /companies — companies the current user can see, for the business
	 * pickers. per_page=999 sidesteps pagination for the dropdown.
	 */
	async listCompanies(): Promise<CompanyOption[]> {
		const res = await api.get<any>('/companies', { per_page: '999' });
		// /companies returns a paginated payload ({ data: [...] }); fall back to a
		// bare array just in case. The company PK is `company_id` (snake_case).
		const rows: any[] = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
		return rows.map((c) => ({ id: c.id ?? c.company_id, name: c.name }));
	},

	/**
	 * POST /users — create a user. The backend also emails the new user their
	 * temp password + a sign-in link. Returns the created user and the temp
	 * password to show the creator (prefers the password we sent so the modal
	 * and the email always match).
	 */
	async create(payload: CreateUserPayload): Promise<CreateUserResult> {
		const res = await api.post<any>('/users', payload);
		const body = res.data ?? {};
		const user = camelizeKeys(body.data) as CreatedUser;
		const tempPassword: string | null = body.temp_password ?? payload.password ?? null;
		return { user, tempPassword };
	},
};
